import React, { useState, useEffect, useRef } from "react"; 
import { jwtFetch } from "./utils/apiClient";
import { useParams, useLocation } from "react-router-dom";
import { format } from "date-fns";
import { config, encodeBase64RequestBody, priceFormat } from "./Components/Config";
import './print.css';

const QuotationPrint = () => {
  const { id_quotation } = useParams();
  const location = useLocation();
  const [quotation, setQuotation] = useState(null);
  const [subcompany, setSubcompany] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const contentRef = useRef(null);
  const searchParams = new URLSearchParams(location.search);
  const isPdfExport = searchParams.get("export") === "pdf";
  const signerName = searchParams.get("signerName") || "";
  const signerPosition = searchParams.get("signerPosition") || "";
  const signerDate = searchParams.get("signerDate") || "";

  useEffect(() => {
    if (isPdfExport) {
      document.body.setAttribute("data-pdf-export", "true");
    } else {
      document.body.removeAttribute("data-pdf-export");
      document.body.removeAttribute("data-quotation-ready");
    }

    return () => {
      document.body.removeAttribute("data-pdf-export");
      document.body.removeAttribute("data-quotation-ready");
    };
  }, [isPdfExport]);

  useEffect(() => {
    const pages = calculateTotalPages();
    setTotalPages(pages);
  }, []);

  useEffect(() => {
    if (id_quotation) {
      getQuotation();
    }
  }, [id_quotation]);

  useEffect(() => {
    if (quotation) {
      document.title = 'Quotation ' + quotation.quotation_code;
      getSubcompany();
    }
  }, [quotation]);
  
  useEffect(() => {
    if (subcompany) {
      if (isPdfExport) {
        requestAnimationFrame(() => {
          document.body.setAttribute("data-quotation-ready", "true");
        });
      } else {
        window.print();
      }
    }
  }, [isPdfExport, subcompany]);

  const calculateTotalPages = () => {
    if (!contentRef.current) return 0;
    const pageHeight = 257 * 3.78;
    const contentHeight = contentRef.current.scrollHeight;
    return Math.ceil(contentHeight / pageHeight);
  };

  const getQuotation = async () => {
    try {
      const fetchQuotation = await jwtFetch(config.ENDPOINT + 'get/quotation', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_quotation: id_quotation
        })
      });
      const responseQuotation = await fetchQuotation.json();
      if (responseQuotation.status === 200) {
        setQuotation(responseQuotation.data[0]);
      } else {
        alert(responseQuotation.message);
      }
    } catch (error) {
      console.error('Error fetching quotation:', error);
    }
  };
  
  const getSubcompany = async () => {
    try {
      const requestGetSubcompany = {
        "id_subcompany": quotation.subcompany_id
      }
      const fetchGetSubcompany = await jwtFetch(config.ENDPOINT + 'get/subcompany', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: encodeBase64RequestBody(requestGetSubcompany)
      });
      const responseGetSubcompany = await fetchGetSubcompany.json();
      if (responseGetSubcompany.status === 200) {
        setSubcompany(responseGetSubcompany.data[0]);
      } else {
        alert(responseGetSubcompany.message);
      }
    } catch (error) {
      console.error('Error in getSubcompany:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  return (
    <div>
      {quotation ? (
        <div ref={contentRef}>
          <div className="invoice-container">
            <div className="invoice-header">
              <div className="row align-items-center justify-content-center">
                <div className="col-12">
                  <div className="row mb-4">
                    <div className="col-6">
                      <img className="mb-3" src={quotation.is_kamigawe? "/kamigawe.png":"/polarisweb.png"} width={250} alt={quotation.is_kamigawe? "Kamigawe" : "Polarisweb"}/>
                      <p className="mb-2"><strong>PT Polarisweb</strong></p>
                      <p className="mb-2">
                        Sampoerna Strategic Square, South Tower 16th Floor<br/>
                        Jl. Jend. Sudirman Kav. 45-46 Kel. Karet Semanggi,<br/>
                        Kec. Setiabudi, Jakarta Selatan 12930
                      </p>
                      <p className="text-underline mb-2">{quotation.is_kamigawe? "https://www.kamigawe.id" : "https://www.pointstar.co.id"}</p>
                    </div>
                    <div className="col-6">
                      <p className="fs-38 mt-4 text-center"><strong>Quotation</strong></p>
                      <div className="row mb-1 g-2 justify-content-center">
                        <div className="col-5 text-end"><strong>QT Created Date</strong><span className="ms-1">:</span></div>
                        <div className="col-5">{format(new Date(quotation.initial_date), 'dd MMMM yyyy')}</div>
                      </div>
                      <div className="row mb-1 g-2 justify-content-center">
                        <div className="col-5 text-end"><strong>Effective Date</strong><span className="ms-1">:</span></div>
                        <div className="col-5">{format(new Date(quotation.created_on), 'dd MMMM yyyy')}</div>
                      </div>
                      <div className="row mb-1 g-2 justify-content-center">
                        <div className="col-5 text-end"><strong>Quotation No.</strong><span className="ms-1">:</span></div>
                        <div className="col-5">{quotation.quotation_code}</div>
                      </div>
                      <div className="row mb-1 g-2 justify-content-center">
                        <div className="col-5 text-end"><strong>Expires</strong><span className="ms-1">:</span></div>
                        <div className="col-5">{format(new Date(quotation.quotation_expired_date), 'dd MMMM yyyy')}</div>
                      </div>
                      <div className="row mb-1 g-2 justify-content-center">
                        <div className="col-5 text-end"><strong>Currency</strong><span className="ms-1">:</span></div>
                        <div className="col-5">{quotation.quotation_currency}</div>
                      </div>
                      <div className="row mb-1 g-2 justify-content-center">
                        <div className="col-5 text-end"><strong>Exchange Rate</strong><span className="ms-1">:</span></div>
                        <div className="col-5">{priceFormat(quotation.quotation_exchange_rate, quotation.quotation_currency)}</div>
                      </div>
                      <div className="row mb-1 g-2 justify-content-center">
                        <div className="col-5 text-end"><strong>Sales Rep. Name</strong><span className="ms-1">:</span></div>
                        <div className="col-5">{quotation.sales_name}</div>
                      </div>
                    </div>
                  </div>
                  {subcompany && (
                    <div className="mb-4">
                      <p className="mb-2"><strong>{subcompany.company_billing_name}</strong></p>
                      <p>{subcompany.company_address}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <table className="items-table mb-4">
              <thead>
                <tr>
                  <th style={{textAlign: 'center'}}>#</th>
                  <th style={{textAlign: 'center'}}>Item</th>
                  <th style={{textAlign: 'center'}}>Qty</th>
                  <th style={{textAlign: 'center'}}>Period</th>
                  <th style={{textAlign: 'center'}}>Unit Price</th>
                  <th className="w-200" style={{textAlign: 'center'}}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {quotation.quotation_items && quotation.quotation_items.map((item, i) => (
                  <tr key={i}>
                    <td valign="top" style={{textAlign: 'center'}}>{i + 1}</td>
                    <td valign="top">
                      <strong className={"d-block" + (item.product_display_description ? ' mb-2' : '')}>{item.product_display_name}</strong>                    
                      {item.product_display_description && item.product_display_description.split('\n').map((description, d) => (
                        <div className="row mb-1" key={"description-" + d}>
                          {description.split(':').length > 1 ? (
                            <div className="col-4">{description.split(':')[0].trim()}</div>
                          ) : (
                            <div className="col-12">{description.trim()}</div>
                          )}
                          {description.split(':').length > 1 && (
                            <div className="col-8">
                              <div className="d-flex">
                                <span className="me-2">:</span>
                                <div>{description.split(':').slice(1).join(':').trim()}</div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </td>
                    {/* <td valign="top">
                      <strong className={"d-block" + (item.product_display_description ? ' mb-2' : '')}>{item.product_display_name}</strong>
                      {item.product_display_description.split('\n').map((description, d) => (
                        <p className="mb-0" key={"desc" + d}>{description}</p>
                      ))}
                    </td> */}
                    <td valign="top" style={{textAlign: 'center'}}>{item.product_qty}</td>
                    <td valign="top" style={{textAlign: 'center'}}>
                      {
                        item.product_type === 'Subscription' ? (
                          (item.contract_long_period + " " + (item.contract_long_period > 1 ? ' Months' : ' Month'))
                        ) : 'N/A'
                      }
                    </td>
                    <td valign="top" style={{textAlign: 'right'}}>{priceFormat(item.product_price, item.product_currency)}</td>
                    <td valign="top" style={{textAlign: 'right'}}>
                      {item.product_discount_price && item.product_discount_price !== '0.000' ? (
                        <div>
                          <span className="text-strike text-danger">{priceFormat(item.product_subtotal, item.product_currency)}</span>
                          <br/>
                          <span>{priceFormat(item.product_total, item.product_currency)}</span>
                        </div>
                      ) : (
                        <span>{priceFormat(item.product_total, item.product_currency)}</span>
                      )}
                    </td>
                  </tr>
                ))}
                {quotation.quotation_discount_price ? (
                  // <tfoot style={{border: 'none'}}>
                    <tr>
                      {quotation.quotation_discount_price && quotation.quotation_discount_price !== '0.000' ? (
                        <td colSpan={5} style={{border: 'none', textAlign: 'right'}}>
                          <strong>Subtotal</strong>
                          <br/>
                          <span className="text-danger"><strong>Discount</strong></span>
                          <br/>
                          <strong>Total</strong>
                          <span className="ms-1">(before applicable tax)</span>
                        </td>
                      ) : (
                        <td colSpan={5} style={{border: 'none', textAlign: 'right'}}>
                          <strong>Total</strong>
                          <span className="ms-1">(before applicable tax)</span>
                        </td>
                      )}
                      {quotation.quotation_discount_price && quotation.quotation_discount_price !== '0.000' ? (
                        <td style={{border: 'none', textAlign: 'right'}}>
                          {priceFormat(quotation.quotation_subtotal, quotation.quotation_currency)}
                          <br/>
                          <span className="text-danger">{"-" + priceFormat(quotation.quotation_discount_price, quotation.quotation_currency)}</span>
                          <br/>
                          {priceFormat(quotation.quotation_total, quotation.quotation_currency)}
                        </td>
                      ) : (
                        <td style={{border: 'none', textAlign: 'right'}}>
                          {priceFormat(quotation.quotation_total, quotation.quotation_currency)}
                        </td>
                      )}
                    </tr>
                  // </tfoot>
                ) : (
                  // <tfoot style={{border: 'none'}}>
                    <tr>
                      <td colSpan={5} style={{border: 'none', textAlign: 'right'}}>
                        <strong>Total</strong>
                        <span className="ms-1">(before applicable tax)</span>
                      </td>
                      <td style={{border: 'none', textAlign: 'right'}}>
                        {priceFormat(quotation.quotation_total, quotation.quotation_currency)}
                      </td>
                    </tr>
                  // </tfoot>
                )}
              </tbody>
            </table>
            
            <div className="terms-section-2">
              <p className="mb-2 fs-20"><strong>Payment Schedule</strong></p>
              <table className="items-table mb-2">
                <thead>
                  <tr>
                    <th style={{textAlign: 'center'}}>Schedule</th>
                    {/* <th style={{textAlign: 'center'}}>Period</th> */}
                    <th style={{textAlign: 'center'}}>Date</th>
                    <th style={{textAlign: 'center'}}>Item</th>
                    <th className="w-200" style={{textAlign: 'center'}}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {quotation.quotation_items && (
                    (() => {
                      const termGroups = {};
                      quotation.quotation_items.forEach((item) => {
                        item.payment_simulations.forEach((payment) => {
                          const key = `${payment.term_number}-${payment.number_of_months}`;
                          if (!termGroups[key]) {
                            termGroups[key] = {
                              term_number: payment.term_number,
                              number_of_months: payment.number_of_months,
                              start_term_period: payment.start_term_period,
                              end_term_period: payment.end_term_period,
                              items: [],
                            };
                          }
                          termGroups[key].items.push({
                            product_display_name: item.product_display_name,
                            payment_amount_exc_tax: payment.payment_amount_exc_tax,
                          });
                        });
                      });

                      const sortedTerms = Object.values(termGroups).sort((a, b) => a.term_number - b.term_number);

                      return sortedTerms.map((term, termIndex) => {
                        const rowspan = term.items.length;
                        return term.items.map((item, itemIndex) => (
                          <tr key={`term-${termIndex}-item-${itemIndex}`}>
                            {itemIndex === 0 && (
                              <>
                                <td style={{ textAlign: 'center' }} rowSpan={rowspan}>
                                  {term.term_number.replace('Term', 'Schedule')}
                                </td>
                                {/* <td style={{ textAlign: 'center' }} rowSpan={rowspan}>
                                  {term.number_of_months + (term.number_of_months > 1 ? ' Months' : ' Month')}
                                </td> */}
                                {/* <td style={{ textAlign: 'center' }} rowSpan={rowspan} valign="middle">
                                  {format(new Date(term.start_term_period), 'd MMM yyyy') !== format(new Date(term.end_term_period), 'd MMM yyyy') ? (
                                    <span>
                                      {(format(new Date(term.start_term_period), 'd MMM yyyy')) + " - " + (format(new Date(term.end_term_period), 'd MMM yyyy'))}
                                    </span>
                                  ) : (
                                    <span>{format(new Date(term.start_term_period), 'd MMM yyyy')}</span>
                                  )}
                                </td> */}
                                <td style={{ textAlign: 'center' }} rowSpan={rowspan} valign="middle">
                                  <span>
                                    {(format(new Date(term.start_term_period), 'd MMM yyyy')) + " - " + (format(new Date(term.end_term_period), 'd MMM yyyy'))}
                                  </span>
                                </td>
                              </>
                            )}
                            <td>{item.product_display_name}</td>
                            <td style={{ textAlign: 'right' }}>
                              {priceFormat(item.payment_amount_exc_tax, quotation.quotation_currency)}
                            </td>
                          </tr>
                        ));
                      }).flat();
                    })()
                  )}
                {/* <tfoot style={{ border: 'none' }}> */}
                  <tr>
                    <td colSpan={3} style={{ border: 'none', textAlign: 'right' }}>
                      <strong>Total</strong>
                      <span className="ms-1">(before applicable tax)</span>
                    </td>
                    <td style={{ border: 'none', textAlign: 'right' }}>
                      {priceFormat(quotation.quotation_total, quotation.quotation_currency)}
                    </td>
                  </tr>
                {/* </tfoot> */}
                </tbody>
              </table>
            </div>
            {quotation.quotation_exchange_floating === 'Floating' && (
              <p className="mb-4">
                *The payment date and amount above are estimates. Actual billing will be based on the exchange rate effective on the invoice date.
              </p>
            )}

            {quotation.subscription_terms && quotation.quotation_items.some(item => item.product_type === 'Subscription') && (
              <div className="terms-section">
                <p className="mb-2 fs-20"><strong>Terms &amp; Conditions</strong></p>
                <p className="mb-0">By executing this order, the Customer agrees to the following terms:</p>
                <ol type="1" style={{ textAlign: 'justify'}}>
                  {quotation.subscription_terms && quotation.subscription_terms.split('\n').map((term, i) => (
                    <li key={'subscription' + i}>{term}</li>
                  ))}
                </ol>
              </div>
            )}

            {quotation.payment_terms && (
              <div className="terms-section">
                <p className="mb-2 fs-20"><strong>Payment Terms</strong></p>
                <p className="mb-0">For any billing, whether in full or in installments, the customer, whose legal name appears on the billing documents, hereby agrees to the following terms:</p>
                <ol type="1" style={{ textAlign: 'justify'}}>
                  {quotation.payment_terms.split('\n').map((term, i) => (
                    <li key={'payment' + i}>{term}</li>
                  ))}
                </ol>
              </div>
            )}

            {quotation.cancellation_terms && (
              <div className="terms-section">
                <p className="mb-2 fs-20"><strong>Cancellation Terms</strong></p>
                <p className="mb-0">In the event that the Customer elects to terminate this Agreement for any reason after execution, the Customer shall:</p>
                <ol type="1" style={{ textAlign: 'justify'}}>
                  {quotation.cancellation_terms.split('\n').map((term, i) => (
                    <li key={'cancellation' + i}>{term}</li>
                  ))}
                </ol>
              </div>
            )}

            <div className="terms-section">
              <p className="mb-2 fs-20"><strong>Acceptance</strong></p>
              <p className="pb-5">We hereby confirm our acceptance of this quotation and agree to be legally bound by the terms and conditions stated in this document.</p>
              <hr className="mt-5 w-300" data-signature-line="true"/>
              <p className="mb-0">Name: {isPdfExport ? signerName : ""}</p>
              <p className="mb-0">Designation: {isPdfExport ? signerPosition : ""}</p>
              {/* <p className="mb-0">Date: {isPdfExport ? signerDate : ""}</p> */}
              {!isPdfExport && (<p className="mb-0">Date: </p>)}
            </div>
          </div>
        </div>
      ) : (
        <div>Loading...</div>
      )}
    </div>
  );
};

export default QuotationPrint;


